import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import _ from 'lodash';

import useFieldLock from 'hooks/useFieldLock';
import useFieldValueBroadcast from 'hooks/useFieldValueBroadcast';
import useRevertFieldValues from 'hooks/useRevertFieldValues';

import { selectPresenceUserColorMap } from 'store/presence/selectors';

import { BLUR_RELEASE_DELAY_MS, IMMEDIATE_INPUT_TYPES } from '../FieldPresenceWrapper.constants';
import {
  getCommittedBroadcastDecisionForFormValueChange,
  getInputValue,
  normalizeFormValue,
  wrapperContainsFocus,
} from '../FieldPresenceWrapper.utils';

const useFieldPresenceBehavior = ({ fieldPath, groupLock, formValue, wrapperRef }) => {
  const {
    lock: directLock,
    isLockedByAnotherUser: isDirectlyLocked,
    isLockedByMe,
    acquireLock,
    releaseLock,
  } = useFieldLock(fieldPath);

  const lock = directLock || groupLock;
  const isLockedByAnotherUser = isDirectlyLocked || !!groupLock;

  const normalizedPath = fieldPath?.toLowerCase();

  const userColorMap = useSelector(selectPresenceUserColorMap);

  const { broadcastChanging, broadcastCommitted } = useFieldValueBroadcast(fieldPath);
  const revertFieldValues = useRevertFieldValues();

  const lastInputValueRef = useRef(null);
  const hasEditedRef = useRef(false);
  const focusValueRef = useRef(null);
  const blurReleaseTimerRef = useRef(null);
  const isLocalEditorRef = useRef(false);
  const hasBeenFocusedRef = useRef(false);

  const formValueRef = useRef(formValue);
  const prevFormValueRef = useRef(formValue);

  formValueRef.current = formValue;

  useEffect(
    () => () => {
      if (blurReleaseTimerRef.current) {
        clearTimeout(blurReleaseTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!isLockedByAnotherUser) {
      return;
    }

    if (wrapperContainsFocus(wrapperRef.current)) {
      hasEditedRef.current = false;
      isLocalEditorRef.current = false;
      lastInputValueRef.current = null;
      focusValueRef.current = null;
      document.activeElement?.blur();
    }
  }, [isLockedByAnotherUser, wrapperRef]);

  useEffect(() => {
    const prevValue = prevFormValueRef.current;

    prevFormValueRef.current = formValue;

    if (hasEditedRef.current) {
      return;
    }

    if (isLockedByAnotherUser || !hasBeenFocusedRef.current) {
      return;
    }

    const { shouldBroadcast, value } = getCommittedBroadcastDecisionForFormValueChange(
      prevValue,
      formValue,
    );

    if (!shouldBroadcast) {
      return;
    }

    hasEditedRef.current = true;
    acquireLock();
    broadcastCommitted(value);
  }, [formValue, isLockedByAnotherUser, broadcastCommitted, acquireLock]);

  const prevLockedByAnotherRef = useRef(isLockedByAnotherUser);

  useEffect(() => {
    const wasLockedByAnother = prevLockedByAnotherRef.current;

    prevLockedByAnotherRef.current = isLockedByAnotherUser;

    if (
      wasLockedByAnother &&
      !isLockedByAnotherUser &&
      !isLockedByMe &&
      wrapperContainsFocus(wrapperRef.current)
    ) {
      acquireLock();
    }
  }, [isLockedByAnotherUser, isLockedByMe, acquireLock, wrapperRef]);

  const onFocusField = useCallback(
    event => {
      if (isLockedByAnotherUser) {
        return;
      }

      if (blurReleaseTimerRef.current) {
        clearTimeout(blurReleaseTimerRef.current);
        blurReleaseTimerRef.current = null;
      }

      isLocalEditorRef.current = true;
      hasBeenFocusedRef.current = true;

      if (_.isNil(focusValueRef.current)) {
        focusValueRef.current = formValueRef.current ?? getInputValue(event.target);
      }

      acquireLock();
    },
    [isLockedByAnotherUser, acquireLock],
  );

  const onBlurField = useCallback(
    event => {
      if (isLockedByAnotherUser) {
        return;
      }

      if (!hasEditedRef.current) {
        const normalizedCurrent = normalizeFormValue(formValueRef.current);
        const normalizedOriginal = normalizeFormValue(focusValueRef.current);

        if (
          !_.isNil(normalizedOriginal) &&
          !_.isNil(normalizedCurrent) &&
          normalizedCurrent !== normalizedOriginal
        ) {
          return;
        }

        blurReleaseTimerRef.current = setTimeout(() => {
          if (!hasEditedRef.current) {
            isLocalEditorRef.current = false;
            releaseLock();
            lastInputValueRef.current = null;
          }
        }, BLUR_RELEASE_DELAY_MS);

        return;
      }

      const normalizedCurrent = normalizeFormValue(formValueRef.current);
      const domValue = getInputValue(event.target) ?? lastInputValueRef.current;
      const value = normalizedCurrent ?? domValue;

      const normalizedFocusValue = normalizeFormValue(focusValueRef.current);

      lastInputValueRef.current = null;

      if (!_.isNil(normalizedFocusValue) && !_.isNil(value) && value === normalizedFocusValue) {
        hasEditedRef.current = false;
        focusValueRef.current = null;
        isLocalEditorRef.current = false;
        releaseLock();

        if (normalizedPath) {
          revertFieldValues([normalizedPath]);
        }

        return;
      }

      if (!_.isNil(value)) {
        broadcastCommitted(value);
      }
    },
    [broadcastCommitted, isLockedByAnotherUser, releaseLock, normalizedPath, revertFieldValues],
  );

  const onInputChange = useCallback(
    event => {
      if (isLockedByAnotherUser) {
        return;
      }

      const { target } = event;
      const value = getInputValue(target);

      if (_.isNil(value)) {
        return;
      }

      lastInputValueRef.current = value;
      hasEditedRef.current = true;

      if (IMMEDIATE_INPUT_TYPES.includes(target.type)) {
        acquireLock();
        broadcastCommitted(String(value));

        return;
      }

      if (!isLockedByMe) {
        return;
      }

      broadcastChanging(String(value));
    },
    [isLockedByAnotherUser, isLockedByMe, acquireLock, broadcastChanging, broadcastCommitted],
  );

  const lockUserColorSet = isLockedByAnotherUser ? userColorMap[lock?.connectionId] : null;

  return {
    lock,
    isLockedByAnotherUser,
    lockUserColorSet,
    onFocusField,
    onBlurField,
    onInputChange,
  };
};

export default useFieldPresenceBehavior;
